import {
  ClockCircleOutlined,
  HourglassOutlined,
  MessageOutlined,
  StarFilled,
} from '@ant-design/icons';
import {CsatStars} from '../components/CsatStars';
import {CLOSER_TERMS, Explained, Info, TERMS} from './Info';
import {
  firstResponseStatus,
  formatAsOf,
  formatCount,
  formatCoverage,
  formatDuration,
  formatTarget,
} from './performanceUtils';
import styles from './performance.module.scss';

// Below this many ratings, an average says little; the tile says so.
const FEW_RATINGS = 5;

const share = (part, whole) => (whole > 0 ? (part / whole) * 100 : 0);

const hasValue = value => value !== null && value !== undefined;

// Who closed the resolved tickets, largest first, as one stacked bar.
const CLOSERS = [
  {key: 'customerNoHelp', label: 'Customer closed it', tone: 'toneCustomer'},
  {key: 'ai', label: 'Sassandra (AI)', tone: 'toneAi'},
  {key: 'faq', label: 'FAQ article', tone: 'toneFaq'},
  {key: 'human', label: 'Support agent', tone: 'toneHuman'},
];

const ResolvedBreakdown = ({closedBy, resolved}) => {
  const parts = CLOSERS.map(closer => ({
    ...closer,
    count: closedBy?.[closer.key] || 0,
  })).sort((a, b) => b.count - a.count);
  return (
    <div className={styles.breakdown}>
      <div className={styles.breakdownBar} aria-hidden="true">
        {parts.map(part =>
          part.count ? (
            <span
              key={part.key}
              className={`${styles.breakdownSegment} ${styles[part.tone]}`}
              style={{width: `${share(part.count, resolved)}%`}}
              title={`${part.label}: ${formatCount(part.count)}`}
            />
          ) : null,
        )}
      </div>
      <ul className={styles.breakdownLegend}>
        {parts.map(part => (
          <li key={part.key}>
            <span className={`${styles.legendDot} ${styles[part.tone]}`} />
            <span className={styles.legendLabel}>
              <Explained text={CLOSER_TERMS[part.key]}>{part.label}</Explained>
            </span>
            <strong>{formatCount(part.count)}</strong>
            <span className={styles.legendShare}>
              {Math.round(share(part.count, resolved))}%
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

// 5★ first, as the reference design reads. The five add up to ratingCount.
const STARS = [
  {key: 'fiveStars', label: '5★'},
  {key: 'fourStars', label: '4★'},
  {key: 'threeStars', label: '3★'},
  {key: 'twoStars', label: '2★'},
  {key: 'oneStar', label: '1★'},
];

export const RatingBars = ({distribution, ratingCount}) => (
  <ul className={styles.ratingBars} aria-label="Ratings by stars">
    {STARS.map(star => {
      const count = distribution?.[star.key] || 0;
      return (
        <li key={star.key}>
          <span className={styles.ratingBarLabel}>{star.label}</span>
          <span className={styles.ratingBarTrack}>
            <span
              className={styles.ratingBarFill}
              style={{width: `${share(count, ratingCount)}%`}}
            />
          </span>
          <span className={styles.ratingBarCount}>{formatCount(count)}</span>
        </li>
      );
    })}
  </ul>
);

/** The backend's verdict on the first-response average, as a small chip. */
export const ResponseStatus = ({firstResponse}) => {
  const {label, tone} = firstResponseStatus(firstResponse);
  return (
    <span className={`${styles.responseStatus} ${styles[`status_${tone}`]}`}>
      {label}
    </span>
  );
};

/** One headline measure: icon, label with its explanation, value, context. */
// While loading, a footer would state "0 of 0 rated" or "Not available"
// about numbers that simply haven't arrived yet — show a placeholder instead.
const Tile = ({icon, tone, label, term, children, footer, loading}) => (
  <div className={`${styles.tile} ${styles[tone]}`}>
    <div className={styles.tileHead}>
      <span className={styles.tileIcon}>{icon}</span>
      <Explained text={term}>{label}</Explained>
    </div>
    <div className={styles.tileBody}>{children}</div>
    {footer && (
      <div className={styles.tileFooter}>
        {loading ? <span className={styles.skeletonLine} /> : footer}
      </div>
    )}
  </div>
);

const MedianLine = ({seconds, measured}) => (
  <span className={styles.smallNote}>
    <Explained text={TERMS.median}>
      Median <strong>{formatDuration(seconds)}</strong>
    </Explained>
    {measured !== undefined && (
      <>
        {' '}
        · from {formatCount(measured || 0)} ticket
        {measured === 1 ? '' : 's'}
      </>
    )}
  </span>
);

/**
 * The report's headline, most important first:
 *
 *   Quality and speed — how customers rated the help and how long it took.
 *   In this period    — how many came in, were resolved, and by whom.
 *   Right now         — what is waiting at this moment. Live, whatever the
 *                       period, so it is set apart and says when it was
 *                       measured.
 */
export const MetricCards = ({
  metrics,
  firstResponse,
  pendingAsOf,
  pendingTerm = TERMS.pending,
  closedBy,
  loading,
  targetAction,
}) => {
  const skeleton = <span className={styles.skeleton} />;
  const resolved = metrics?.resolved || 0;
  const rated = metrics?.ratingCount || 0;
  const coverage = metrics?.ratingCoverage ?? 0;
  const asOf = formatAsOf(pendingAsOf);
  const hasRating = hasValue(metrics?.averageRating);

  return (
    <div className={styles.summary}>
      <section className={`${styles.panel} ${styles.qualityPanel}`}>
        <h3 className={styles.panelTitle}>Quality and speed</h3>
        <div className={styles.tiles}>
          <Tile
            loading={loading}
            icon={<StarFilled />}
            tone="tileRating"
            label="Customer rating"
            term={TERMS.rating}
            footer={
              <>
                {rated > 0 && (
                  <RatingBars
                    distribution={metrics?.ratingDistribution}
                    ratingCount={rated}
                  />
                )}
                <div
                  className={styles.coverageTrack}
                  aria-label={`${formatCoverage(
                    coverage,
                  )} of resolved tickets rated`}>
                  <span
                    className={styles.coverageFill}
                    style={{width: `${Math.min(100, coverage)}%`}}
                  />
                </div>
                <span className={styles.smallNote}>
                  <Explained text={TERMS.coverage}>
                    {formatCount(rated)} of {formatCount(resolved)} resolved
                    tickets rated ({formatCoverage(coverage)})
                  </Explained>
                </span>
              </>
            }>
            {loading ? (
              skeleton
            ) : hasRating ? (
              <span className={styles.ratingValue}>
                {Number(metrics.averageRating).toFixed(2)}
                <span className={styles.outOf}>/ 5</span>
                <CsatStars
                  score={Math.round(metrics.averageRating)}
                  size="md"
                  showNumeric={false}
                />
              </span>
            ) : (
              <span className={styles.mutedValue}>No ratings yet</span>
            )}
            {!loading && hasRating && rated < FEW_RATINGS && (
              <span className={styles.caution}>
                Based on only {rated} rating{rated === 1 ? '' : 's'} — treat
                with care
              </span>
            )}
          </Tile>

          {firstResponse !== undefined && (
            <Tile
              loading={loading}
              icon={<MessageOutlined />}
              tone="tileResponse"
              label="First human response"
              term={TERMS.firstResponse}
              footer={
                <>
                  <span className={styles.smallNote}>
                    Target{' '}
                    <strong>
                      {formatTarget(firstResponse?.targetSeconds)}
                    </strong>
                    {targetAction}
                  </span>
                  <MedianLine
                    seconds={firstResponse?.medianSeconds}
                    measured={firstResponse?.measuredTickets}
                  />
                </>
              }>
              {loading ? (
                skeleton
              ) : (
                <span
                  className={
                    hasValue(firstResponse?.averageSeconds)
                      ? styles.timeValue
                      : styles.mutedValue
                  }>
                  {formatDuration(firstResponse?.averageSeconds)}
                </span>
              )}
              <span className={styles.valueCaption}>
                average{' '}
                {!loading && <ResponseStatus firstResponse={firstResponse} />}
              </span>
            </Tile>
          )}

          <Tile
            loading={loading}
            icon={<HourglassOutlined />}
            tone="tileQueue"
            label="Human queue turnaround"
            term={TERMS.turnaround}
            footer={
              <MedianLine
                seconds={metrics?.medianAgentSeconds}
                measured={metrics?.agentTimeCount}
              />
            }>
            {loading ? (
              skeleton
            ) : (
              <span
                className={
                  hasValue(metrics?.averageAgentSeconds)
                    ? styles.timeValue
                    : styles.mutedValue
                }>
                {formatDuration(metrics?.averageAgentSeconds)}
              </span>
            )}
            <span className={styles.valueCaption}>average</span>
          </Tile>

          <Tile
            loading={loading}
            icon={<ClockCircleOutlined />}
            tone="tileWait"
            label="Resolution time (calendar)"
            term={TERMS.customerWait}
            footer={
              <MedianLine
                seconds={metrics?.medianCustomerWaitSeconds}
                measured={metrics?.customerTimeCount}
              />
            }>
            {loading ? (
              skeleton
            ) : (
              <span
                className={
                  hasValue(metrics?.averageCustomerWaitSeconds)
                    ? styles.timeValue
                    : styles.mutedValue
                }>
                {formatDuration(metrics?.averageCustomerWaitSeconds)}
              </span>
            )}
            <span className={styles.valueCaption}>average</span>
          </Tile>
        </div>
      </section>

      <div className={styles.summaryRow}>
        <section className={`${styles.panel} ${styles.periodPanel}`}>
          <h3 className={styles.panelTitle}>In this period</h3>
          <div className={styles.bigNumbers}>
            <div>
              <Explained text={TERMS.opened}>
                <span className={styles.bigLabel}>Tickets opened</span>
              </Explained>
              <span className={styles.bigValue}>
                {loading ? skeleton : formatCount(metrics?.opened)}
              </span>
            </div>
            <div>
              <Explained text={TERMS.resolved}>
                <span className={styles.bigLabel}>Resolved</span>
              </Explained>
              <span className={styles.bigValue}>
                {loading ? skeleton : formatCount(metrics?.resolved)}
              </span>
            </div>
            <div>
              <Explained text={TERMS.abandoned}>
                <span className={styles.bigLabel}>Abandoned</span>
              </Explained>
              <span className={styles.bigValue}>
                {loading ? skeleton : formatCount(metrics?.abandoned)}
              </span>
            </div>
          </div>
          {closedBy && !loading && resolved > 0 && (
            <>
              <p className={styles.panelSubtitle}>
                Who resolved them{' '}
                <Info text="Every resolved ticket, by who closed it. The four add up to Resolved." />
              </p>
              <ResolvedBreakdown closedBy={closedBy} resolved={resolved} />
            </>
          )}
        </section>

        <section className={`${styles.panel} ${styles.livePanel}`}>
          <h3 className={styles.panelTitle}>
            <span className={styles.liveDot} /> Right now
          </h3>
          <Explained text={pendingTerm}>
            <span className={styles.bigLabel}>Pending now</span>
          </Explained>
          <span className={styles.bigValue}>
            {loading ? skeleton : formatCount(metrics?.pending)}
          </span>
          {hasValue(metrics?.unassignedQueue) && (
            <p className={styles.liveDetail}>
              <Explained text={TERMS.unassigned}>
                <span>
                  <strong>{formatCount(metrics.unassignedQueue)}</strong> not
                  yet picked up by an agent
                </span>
              </Explained>
            </p>
          )}
          {hasValue(firstResponse?.unansweredTickets) && (
            <p className={styles.liveDetail}>
              <Explained text={TERMS.unanswered}>
                <span>
                  <strong>
                    {formatCount(firstResponse.unansweredTickets)}
                  </strong>{' '}
                  waiting for a first reply
                  {firstResponse.overdueUnansweredTickets > 0 && (
                    <>
                      {' · '}
                      <strong className={styles.overdue}>
                        {formatCount(firstResponse.overdueUnansweredTickets)}{' '}
                        past target
                      </strong>
                    </>
                  )}
                </span>
              </Explained>
            </p>
          )}
          <p className={styles.liveNote}>
            Live count{asOf ? `, updated ${asOf}` : ''}. The dates above
            don&apos;t change it.
          </p>
        </section>
      </div>
    </div>
  );
};
